import React, {useState} from 'react';
import moment from 'moment';
import omit from 'lodash/omit';

import Form from '../common/Form';

import useUser from '../../hooks/useUser';
import {
  useCreateEventMutation, 
  useUpdateEventMutation
} from '../../redux/services/eventApi';
import {getEventCategories} from '../../helpers/selectOptions';

const sections = () => ([
  {
    name: 'general',
    label: '🗓️ General',
  },
  {
    name: 'recurring',
    label: '🔁 Recurring Event',
    prerequisites: ['general'],
  },
  {
    name: 'oneTime',
    label: '⏱️ One-Time Event',
    prerequisites: ['general'],
  },
  {
    name: 'settings',
    label: '⚙️ Settings',
    prerequisites: ['general'],
  }
]);

const isBeforeToday = dayMomentObj => {
  if (!dayMomentObj) return false;
  const today = moment().startOf('day');
  return dayMomentObj.isBefore(today);
}

const getFields = ({
  isRecurring,
  setIsRecurring,
  provider,
}) => [
  {
    name: 'title',
    section: 'general',
    label: 'Title',
    displayName: 'Title',
    type: 'text',
    placeholder: 'Add a title...',
    required: true,
    autoFocus: true,
    validate: str => true,
    grid: {xs: 12, md: 12},
  },
  // {
  //   name: 'event_category',
  //   section: 'general',
  //   label: 'Event Type',
  //   type: 'select',
  //   defaultValue: 'ADMIN_TIME',
  //   options: getEventCategories(),
  //   grid: {
  //     xs: 12,
  //     md: 6,
  //   }
  // },
  {
    name: 'is_recurring',
    section: 'general',
    label: 'Frequency',
    type: 'choice',
    defaultValue: false,
    options: [
      {label: 'One Time Event', value: false},
      {label: 'Recurring Event', value: true},
    ],
    onChange: (e, val) => setIsRecurring(val),
    grid: {
      xs: 12,
      md: 6,
    }
  },
  ...(
    isRecurring 
    ? [
      {
        name: 'recurring_days',
        section: 'recurring',
        label: 'Days',
        type: 'daysOfWeek',
        required: true,
        multi: true,
        color: 'info',
        spacing: 0.125,
        grid: {
          md: 6,
          xs: 12,
        }
      },
      {
        name: 'repeat_every_period_value',
        section: 'recurring',
        type: 'choice',
        label: 'Repeat Every',
        required: true,
        defaultValue: 1,
        color: 'purple',
        options: [
          {label: '1 Week', value: 1},
          {label: '2 Weeks', value: 2},
          // {label: '3 Weeks', value: 3},
          {label: '4 Weeks', value: 4},
          // {label: '5 Weeks', value: 5},
          // {label: '6 Weeks', value: 6},
          // {label: '7 Weeks', value: 7},
          // {label: '8 Weeks', value: 8},
        ],
        grid: {
          md: 6,
          xs: 12,
        }
      },
      {
        name: 'recurring_start_time',
        section: 'recurring',
        label: 'Start Time',
        type: 'time',
        required: true,
        color: 'info',
        placeholder: '9:00 am',
        maxWidth: '120px',
        grid: {
          md: 3,
          xs: 6,
        }
      },
      {
        name: 'duration',
        section: 'recurring',
        label: 'Duration (min)',
        type: 'number',
        required: true,
        color: 'info',
        placeholder: '60',
        maxWidth: '130px',
        grid: {
          md: 3,
          xs: 6,
        }
      },
      {
        name: 'start_time',
        section: 'recurring',
        type: 'date',
        label: 'Begins On',
        color: 'info',
        isOutsideRange: isBeforeToday,
        required: false,
        grid: {
          md: 3,
          xs: 6,
        },
        parser: value => (
          moment(value, 'MM/DD/yyyy').startOf('day').toISOString()
        ),
      },
      {
        name: 'end_time',
        section: 'recurring',
        type: 'date',
        label: 'Stops On',
        color: 'info',
        isOutsideRange: isBeforeToday,
        required: false,
        grid: {
          md: 3,
          xs: 6,
        }
      },
    ]
    : [
      {
        name: 'start_time',
        section: 'oneTime',
        type: 'datetime',
        label: 'Start Time',
        color: 'info',
        isOutsideRange: isBeforeToday,
        grid: {
          md: 6,
          xs: 12,
        }
      },
      {
        name: 'duration',
        section: 'oneTime',
        type: 'number',
        color: 'info',
        label: 'Duration (min)',
        placeholder: '60',
        maxWidth: '130px',
        grid: {
          md: 6,
          xs: 12,
        }
      },
    ]
  ),
  {
    name: 'notes',
    section: 'settings',
    label: 'Notes',
    type: 'textarea',
    placeholder: 'Optional notes...',
    rows: 2,
    grid: {
      md: 12,
      xs: 12,
    },
  }
];


const getDefaultValues = ({
  doc,
  isRecurring,
  provider, 
}) => {
  const duration = (
    isRecurring ? (
      // calculate duration from recurring_start_time and recurring_end_time if available, 
      // these are durations from the start of the day presented like 17:00:00 for 5pm
      // otherwise default to 60
        doc?.recurring_start_time && doc?.recurring_end_time
        ? moment(doc.recurring_end_time, 'HH:mm:ss').diff(moment(doc.recurring_start_time, 'HH:mm:ss'), 'minutes')
        : 60
    ).toString() : (
      doc?.start_time && doc?.end_time 
      ? moment(doc.end_time).diff(moment(doc.start_time), 'minutes') 
      : 60
    ).toString()
  );
  let newDoc = (
    isRecurring
    ? {
      event_category: 'ADMIN_TIME',
      title: doc?.title || 'Meeting',
      is_recurring: true,
      recurring_days: doc?.recurring_days || [],
      repeat_every_period_value: doc?.repeat_every_period_value || 1,
      recurring_start_time: doc?.recurring_start_time || null,
      recurring_end_time: doc?.recurring_end_time || null,
      start_time: doc?.start_time || null,
      duration: duration || 60,
    }
    : {
      event_category: 'ADMIN_TIME',
      title: doc?.title || 'Meeting',
      is_recurring: false,
      start_time: doc?.start_time || null,
      recurring_days: null,
      repeat_every_period_value: null,
      recurring_start_time: null,
      duration: duration || 60,
    }
  );
  return newDoc;
};

const serializer = data => {
  let body = {
    ...omit(data, 'duration'),
    start_time: (
      data?.start_time ? moment(data?.start_time, 'MM/DD/yyyy').startOf('day').toISOString()
      : data.is_recurring ? moment().startOf('day').toISOString()
      : null
    ),
  };
  console.log('serializer data', data);
  if (body.is_recurring) {
    body.recurring_end_time = moment(data?.recurring_start_time, 'HH:mm:ss')
      .add(parseInt(data.duration, 10), 'minutes')
      .format('HH:mm:ss');
  } else {
    body.end_time = moment(body.start_time)
      .add(parseInt(data.duration, 10), 'minutes')
      .toISOString();
  }
  return body;
};

const handleSubmit = ({
  isEditing, 
  providerId, 
  send,
  onSuccess, 
  id,
  eventCategory = 'ADMIN_TIME',
} = {}) => async formData => {
  const body = (
    isEditing ? {
      id,
      body: formData,
    }
    : {
      ...formData, 
      // session_type_version: '2.0',
      provider_id: providerId,
      event_category: eventCategory,
    }
  );
  const request = await send(body);
  if (!request?.error && onSuccess) onSuccess(request);
  // return a failed promise if there was an error
  if (request?.error) return Promise.reject(new Error(request?.error?.data?.errors?.[0]));
  return Promise.resolve(request);
};

const EventForm = ({
  doc = null, // existing document
  isEditing = false,
  backgroundColor = '#ffffff',
  serviceCategoriesUsed = [],
  onSuccess = e => console.log('onSuccess', e),
}) => {
  const [isRecurring, setIsRecurring] = useState(doc?.is_recurring || false);
  const user = useUser();
  const [post] = useCreateEventMutation();
  const [put] = useUpdateEventMutation();
  if (!user?.provider) return null;
  const fields = getFields({
    isRecurring, 
    setIsRecurring,
    provider: user.provider
  });
  const send = isEditing ? put : post;
  const onSubmit = handleSubmit({
    isEditing,
    id: doc?.id,
    send,
    onSuccess,
    providerId: user.provider.id,
  });
  return (
    <Form
      key={(doc?.id || 'new') + '-' + isRecurring ? 'recurring' : 'onetime'}
      defaultValues={getDefaultValues({
        doc, 
        provider: user.provider,
        serviceCategoriesUsed,
      })}
      sections={sections().filter(s => 
        isRecurring 
        ? s?.name !== 'oneTime' 
        : s?.name !== 'recurring'
      )}
      fields={fields.filter(f => isEditing ? !f?.notEditable : true)}
      backgroundColor={backgroundColor}
      serializer={serializer}
      onSubmit={onSubmit}
    />
  );
};

export default EventForm;